import { useState, useEffect } from 'react';
import { bugController } from '../../controllers/bugController';
import { projectController } from '../../controllers/projectController';
import { taskController } from '../../controllers/taskcontroller';
import { employeeController } from '../../controllers/employeeController';
import { 
  Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle, 
  ChevronUp, ChevronDown, Bug, AlertOctagon, AlertTriangle, 
  CheckCircle2, Clock, XCircle, Filter, BarChart3
} from 'lucide-react';

const BugsPage = () => {
  const [bugs, setBugs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredBugs, setFilteredBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterSeverity, setFilterSeverity] = useState('All');
  const [filterPriority, setFilterPriority] = useState('All');
  const [filterProject, setFilterProject] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBug, setSelectedBug] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    projectId: '',
    taskId: '',
    assignedTo: '',
    status: 'Open',
    severity: 'Medium',
    priority: 'Medium',
    environment: '',
    stepsToReproduce: '',
    expectedBehavior: '',
    actualBehavior: ''
  });
  const [formErrors, setFormErrors] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBugs, setSelectedBugs] = useState([]);
  const itemsPerPage = 10;

  useEffect(() => { 
    fetchBugs(); 
    fetchProjects();
    fetchTasks();
    fetchUsers();
  }, []);

  useEffect(() => {
    let result = [...bugs];
    
    if (searchQuery) {
      result = result.filter(b =>
        (b.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (b.bugId || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (b.description || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (filterStatus !== 'All') {
      result = result.filter(b => b.status === filterStatus);
    }
    
    if (filterSeverity !== 'All') {
      result = result.filter(b => b.severity === filterSeverity);
    }

    if (filterPriority !== 'All') {
      result = result.filter(b => b.priority === filterPriority);
    }
    
    if (filterProject !== 'All') {
      result = result.filter(b => {
        const projectId = b.projectId?._id || b.projectId;
        return projectId === filterProject;
      });
    }
    
    result.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    setFilteredBugs(result);
    setCurrentPage(1);
  }, [bugs, searchQuery, filterStatus, filterSeverity, filterPriority, filterProject, sortField, sortDirection]);

  const fetchBugs = async () => {
    setLoading(true);
    const result = await bugController.getAll();
    if (result.success) {
      setBugs(result.data);
    } else {
      alert('Error: ' + result.message);
    }
    setLoading(false);
  };

  const fetchProjects = async () => {
    const result = await projectController.getAll();
    if (result.success) {
      setProjects(result.data);
    }
  };

  const fetchTasks = async () => {
    const result = await taskController.getAll();
    if (result.success) {
      setTasks(result.data);
    }
  };

  const fetchUsers = async () => {
    const result = await employeeController.getAll();
    if (result.success) {
      setUsers(result.data);
    }
  };

  const getProjectName = (projectId) => {
    if (typeof projectId === 'object' && projectId !== null) {
      return projectId.name || projectId.projectName || 'Unknown Project';
    }
    const project = projects.find(p => (p.id || p._id) === projectId);
    return project ? (project.name || project.projectName) : 'Unknown Project';
  };

  const getTaskName = (taskId) => {
    if (!taskId) return 'N/A';
    if (typeof taskId === 'object' && taskId !== null) {
      return taskId.title || taskId.taskName || 'Unknown Task';
    }
    const task = tasks.find(t => (t.id || t._id) === taskId);
    return task ? (task.title || task.taskName) : 'Unknown Task';
  };

  const getUserName = (userId) => {
    if (!userId) return 'Unassigned';
    if (typeof userId === 'object' && userId !== null) {
      return userId.name || userId.fullName || userId.email || 'Unknown User';
    }
    const user = users.find(u => (u.id || u._id) === userId);
    return user ? (user.name || user.fullName || user.email) : 'Unknown User';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormErrors([]);
  };

  const handleAdd = () => {
    setFormData({
      title: '',
      description: '',
      projectId: '',
      taskId: '',
      assignedTo: '',
      status: 'Open',
      severity: 'Medium',
      priority: 'Medium',
      environment: '',
      stepsToReproduce: '',
      expectedBehavior: '',
      actualBehavior: ''
    });
    setFormErrors([]);
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    
    const dataToSend = { ...formData };
    if (!dataToSend.taskId) delete dataToSend.taskId;
    if (!dataToSend.assignedTo) delete dataToSend.assignedTo;
    
    const result = await bugController.create(dataToSend);
    if (result.success) {
      setShowAddModal(false);
      fetchBugs();
      alert('✅ Bug reported successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    setFormSubmitting(false);
  };

  const handleEdit = (bug) => {
    setSelectedBug(bug);
    
    let projectIdValue = '';
    if (bug.projectId) {
      if (typeof bug.projectId === 'object') {
        projectIdValue = bug.projectId.id || bug.projectId._id;
      } else {
        projectIdValue = bug.projectId;
      }
    }

    let taskIdValue = '';
    if (bug.taskId) {
      if (typeof bug.taskId === 'object') {
        taskIdValue = bug.taskId.id || bug.taskId._id;
      } else {
        taskIdValue = bug.taskId;
      }
    }

    let assignedToValue = '';
    if (bug.assignedTo) {
      if (typeof bug.assignedTo === 'object') {
        assignedToValue = bug.assignedTo.id || bug.assignedTo._id;
      } else {
        assignedToValue = bug.assignedTo;
      }
    }
    
    setFormData({
      title: bug.title || '',
      description: bug.description || '',
      projectId: projectIdValue,
      taskId: taskIdValue,
      assignedTo: assignedToValue,
      status: bug.status || 'Open',
      severity: bug.severity || 'Medium',
      priority: bug.priority || 'Medium',
      environment: bug.environment || '',
      stepsToReproduce: bug.stepsToReproduce || '',
      expectedBehavior: bug.expectedBehavior || '',
      actualBehavior: bug.actualBehavior || ''
    });
    setFormErrors([]);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    
    const dataToSend = { ...formData };
    if (!dataToSend.taskId) delete dataToSend.taskId;
    if (!dataToSend.assignedTo) delete dataToSend.assignedTo;
    
    const result = await bugController.update(selectedBug.id || selectedBug._id, dataToSend);
    if (result.success) {
      setShowEditModal(false);
      fetchBugs();
      alert('✅ Bug updated successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    setFormSubmitting(false);
  };

  const handleView = (bug) => {
    setSelectedBug(bug);
    setShowViewModal(true);
  };

  const handleDelete = async (bug) => {
    try {
      const isConfirmed = window.confirm(
        `Delete bug "${bug.title}"?`
      );
  
      if (!isConfirmed) return;
  
      const result = await bugController.delete(bug.id || bug._id);
  
      if (result?.success) {
        await fetchBugs();
        alert('✅ Bug deleted successfully!');
      } else {
        alert(result?.message || '❌ Failed to delete bug');
      }
    } catch (error) {
      console.error('Delete bug error:', error);
      alert('❌ Something went wrong while deleting bug');
    }
  };

  const handleStatusChange = async (bug, newStatus) => {
    const result = await bugController.updateStatus(bug.id || bug._id, newStatus);
    if (result.success) {
      fetchBugs();
      alert('✅ Status updated!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = async () => {
    const filters = {};
    if (filterStatus !== 'All') filters.status = filterStatus;
    if (filterSeverity !== 'All') filters.severity = filterSeverity;
    if (filterPriority !== 'All') filters.priority = filterPriority;
    if (filterProject !== 'All') filters.projectId = filterProject;
    
    const result = await bugController.export(filters, 'csv');
    if (result.success) {
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bugs-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      alert('✅ Bugs exported successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleSelectBug = (bugId) => {
    setSelectedBugs(prev => 
      prev.includes(bugId) 
        ? prev.filter(id => id !== bugId)
        : [...prev, bugId]
    );
  };

  const handleSelectAll = () => {
    if (selectedBugs.length === currentBugs.length && currentBugs.length > 0) {
      setSelectedBugs([]);
    } else {
      setSelectedBugs(currentBugs.map(b => b.id || b._id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedBugs.length === 0) {
      alert('Please select bugs to delete');
      return;
    }
    
    const confirmed = window.confirm(`Delete ${selectedBugs.length} selected bugs?`);
    if (!confirmed) return;
    
    const result = await bugController.bulkDelete(selectedBugs);
    if (result.success) {
      setSelectedBugs([]);
      fetchBugs();
      alert('✅ Bugs deleted successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedBugs.length === 0) {
      alert('Please select bugs to update');
      return;
    }
    
    const result = await bugController.bulkUpdate(selectedBugs, { status: newStatus });
    if (result.success) {
      setSelectedBugs([]);
      fetchBugs();
      alert('✅ Bugs updated successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentBugs = filteredBugs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredBugs.length / itemsPerPage);

  const stats = {
    total: bugs.length,
    open: bugs.filter(b => b.status === 'Open' || b.status === 'Reopened').length,
    inProgress: bugs.filter(b => b.status === 'In Progress').length,
    resolved: bugs.filter(b => b.status === 'Resolved' || b.status === 'Closed').length,
    critical: bugs.filter(b => b.severity === 'Critical').length
  };

  const styles = {
    container: { padding: 0 },
    header: { 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      marginBottom: '24px', 
      flexWrap: 'wrap', 
      gap: '16px' 
    },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: 0 },
    button: { 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px', 
      padding: '10px 20px', 
      backgroundColor: '#3b82f6', 
      color: '#fff', 
      border: 'none', 
      borderRadius: '8px', 
      fontSize: '14px', 
      fontWeight: '500', 
      cursor: 'pointer' 
    },
    secondaryButton: { 
      backgroundColor: '#fff', 
      color: '#1e293b', 
      border: '1px solid #e2e8f0' 
    },
    dangerButton: {
      backgroundColor: '#ef4444',
      color: '#fff'
    },
    statsBar: { 
      display: 'grid', 
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
      gap: '16px', 
      marginBottom: '24px' 
    },
    statCard: { 
      backgroundColor: '#fff', 
      padding: '20px', 
      borderRadius: '12px', 
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)' 
    },
    statLabel: { fontSize: '14px', color: '#64748b', marginBottom: '8px' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b' },
    filterBar: { 
      display: 'flex', 
      gap: '12px', 
      marginBottom: '20px', 
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    searchBox: { flex: 1, minWidth: '250px', position: 'relative' },
    searchInput: { 
      width: '100%', 
      padding: '10px 10px 10px 40px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none' 
    },
    searchIcon: { 
      position: 'absolute', 
      left: '12px', 
      top: '50%', 
      transform: 'translateY(-50%)' 
    },
    select: { 
      padding: '10px 16px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none', 
      backgroundColor: '#fff', 
      cursor: 'pointer' 
    },
    card: { 
      backgroundColor: '#fff', 
      borderRadius: '12px', 
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
      overflow: 'hidden' 
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { 
      backgroundColor: '#f8fafc', 
      padding: '16px', 
      textAlign: 'left', 
      fontSize: '13px', 
      fontWeight: '600', 
      color: '#475569', 
      borderBottom: '1px solid #e2e8f0', 
      cursor: 'pointer', 
      userSelect: 'none' 
    },
    td: { 
      padding: '16px', 
      borderBottom: '1px solid #e2e8f0', 
      fontSize: '14px', 
      color: '#1e293b' 
    },
    badge: { 
      display: 'inline-block', 
      padding: '4px 12px', 
      borderRadius: '12px', 
      fontSize: '12px', 
      fontWeight: '500' 
    },
    actionButton: { 
      padding: '6px', 
      backgroundColor: 'transparent', 
      border: 'none', 
      borderRadius: '6px', 
      cursor: 'pointer' 
    },
    pagination: { 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      padding: '20px', 
      borderTop: '1px solid #e2e8f0' 
    },
    modal: { 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: 'rgba(0,0,0,0.5)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 1000, 
      padding: '20px' 
    },
    modalContent: { 
      backgroundColor: '#fff', 
      borderRadius: '12px', 
      width: '100%', 
      maxWidth: '900px', 
      maxHeight: '90vh', 
      overflow: 'auto' 
    },
    modalHeader: { 
      padding: '20px 24px', 
      borderBottom: '1px solid #e2e8f0', 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center' 
    },
    modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 },
    modalBody: { padding: '24px' },
    formGroup: { marginBottom: '20px' },
    label: { 
      display: 'block', 
      marginBottom: '8px', 
      fontSize: '14px', 
      fontWeight: '500', 
      color: '#374151' 
    },
    input: { 
      width: '100%', 
      padding: '10px 12px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none' 
    },
    textarea: { 
      width: '100%', 
      padding: '10px 12px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none', 
      minHeight: '100px', 
      resize: 'vertical' 
    },
    errorList: { 
      backgroundColor: '#fee2e2', 
      border: '1px solid #fecaca', 
      borderRadius: '8px', 
      padding: '12px 16px', 
      marginBottom: '20px' 
    },
    errorItem: { 
      color: '#dc2626', 
      fontSize: '14px', 
      marginBottom: '4px', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px' 
    },
    modalFooter: { 
      padding: '16px 24px', 
      borderTop: '1px solid #e2e8f0', 
      display: 'flex', 
      justifyContent: 'flex-end', 
      gap: '12px' 
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer'
    },
    infoSection: {
      marginTop: '20px',
      padding: '16px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0'
    },
    sectionTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '12px'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Bug Tracker</h1>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {selectedBugs.length > 0 && (
            <>
              <button 
                style={{ ...styles.button, ...styles.dangerButton }} 
                onClick={handleBulkDelete}
              >
                <Trash2 size={18} /> Delete ({selectedBugs.length})
              </button>
              <select 
                style={styles.select}
                onChange={(e) => {
                  if (e.target.value) {
                    handleBulkStatusUpdate(e.target.value);
                    e.target.value = '';
                  }
                }}
              >
                <option value="">Update Status</option>
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="Resolved">Resolved</option>
                <option value="Closed">Closed</option>
                <option value="Reopened">Reopened</option>
              </select>
            </>
          )}
          <button 
            style={{ ...styles.button, ...styles.secondaryButton }} 
            onClick={handleExport}
          >
            <Download size={18} /> Export
          </button>
          <button style={styles.button} onClick={handleAdd}>
            <Plus size={18} /> Report Bug
          </button>
        </div>
      </div>

      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Total Bugs</div>
            <Bug size={24} color="#3b82f6" />
          </div>
          <div style={styles.statValue}>{stats.total}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Open</div>
            <AlertCircle size={24} color="#3b82f6" />
          </div>
          <div style={{ ...styles.statValue, color: '#3b82f6' }}>{stats.open}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>In Progress</div>
            <Clock size={24} color="#f59e0b" />
          </div>
          <div style={{ ...styles.statValue, color: '#f59e0b' }}>{stats.inProgress}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Resolved</div>
            <CheckCircle2 size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.resolved}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Critical</div>
            <AlertOctagon size={24} color="#dc2626" />
          </div>
          <div style={{ ...styles.statValue, color: '#dc2626' }}>{stats.critical}</div>
        </div>
      </div>

      <div style={styles.filterBar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input 
            type="text" 
            placeholder="Search bugs..." 
            style={styles.searchInput} 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
          />
        </div>
        <select 
          style={styles.select} 
          value={filterProject} 
          onChange={(e) => setFilterProject(e.target.value)}
        >
          <option value="All">All Projects</option>
          {projects.map(project => (
            <option key={project.id || project._id} value={project.id || project._id}>
              {project.name || project.projectName}
            </option>
          ))}
        </select>
        <select 
          style={styles.select} 
          value={filterStatus} 
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="All">All Status</option>
          <option value="Open">Open</option>
          <option value="In Progress">In Progress</option>
          <option value="Resolved">Resolved</option>
          <option value="Closed">Closed</option>
          <option value="Reopened">Reopened</option>
        </select>
        <select 
          style={styles.select} 
          value={filterSeverity} 
          onChange={(e) => setFilterSeverity(e.target.value)}
        >
          <option value="All">All Severity</option>
          <option value="Critical">Critical</option>
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Low">Low</option>
        </select>
        <select 
          style={styles.select} 
          value={filterPriority} 
          onChange={(e) => setFilterPriority(e.target.value)}
        >
          <option value="All">All Priority</option>
          <option value="Critical">Critical</option>
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Low">Low</option>
        </select>
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            Loading bugs...
          </div>
        ) : currentBugs.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            No bugs found. Click "Report Bug" to add one.
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: '50px' }}>
                    <input 
                      type="checkbox"
                      style={styles.checkbox}
                      checked={selectedBugs.length === currentBugs.length && currentBugs.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th style={styles.th} onClick={() => handleSort('bugId')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      ID {sortField === 'bugId' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th} onClick={() => handleSort('title')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Title {sortField === 'title' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th}>Project</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Severity</th>
                  <th style={styles.th}>Priority</th>
                  <th style={styles.th}>Assigned To</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentBugs.map((bug) => {
                  const getStatusColor = (status) => {
                    const colors = {
                      'Open': '#3b82f6',
                      'In Progress': '#f59e0b',
                      'Resolved': '#10b981',
                      'Closed': '#64748b',
                      'Reopened': '#ef4444'
                    };
                    return colors[status] || '#6b7280';
                  };

                  const getSeverityColor = (severity) => {
                    const colors = {
                      'Critical': '#dc2626',
                      'High': '#f59e0b',
                      'Medium': '#3b82f6',
                      'Low': '#10b981'
                    };
                    return colors[severity] || '#6b7280';
                  };

                  const getPriorityColor = (priority) => {
                    const colors = {
                      'Critical': '#dc2626',
                      'High': '#f59e0b',
                      'Medium': '#3b82f6',
                      'Low': '#10b981'
                    };
                    return colors[priority] || '#6b7280';
                  };
                  
                  return (
                    <tr key={bug.id || bug._id} style={{ cursor: 'pointer' }}>
                      <td style={styles.td}>
                        <input 
                          type="checkbox"
                          style={styles.checkbox}
                          checked={selectedBugs.includes(bug.id || bug._id)}
                          onChange={() => handleSelectBug(bug.id || bug._id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                          {bug.bugId}
                        </div>
                      </td>
                      <td style={styles.td} onClick={() => handleView(bug)}>
                        <div style={{ fontWeight: '500' }}>{bug.title}</div>
                        {bug.description && (
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                            {bug.description.substring(0, 50)}{bug.description.length > 50 ? '...' : ''}
                          </div>
                        )}
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontSize: '13px' }}>
                          {getProjectName(bug.projectId)}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <select
                          style={{
                            ...styles.badge,
                            backgroundColor: `${getStatusColor(bug.status)}20`,
                            color: getStatusColor(bug.status),
                            border: 'none',
                            cursor: 'pointer'
                          }}
                          value={bug.status}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleStatusChange(bug, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="Open">Open</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Resolved">Resolved</option>
                          <option value="Closed">Closed</option>
                          <option value="Reopened">Reopened</option>
                        </select>
                      </td>
                      <td style={styles.td}>
                        <span style={{ 
                          ...styles.badge, 
                          backgroundColor: `${getSeverityColor(bug.severity)}20`, 
                          color: getSeverityColor(bug.severity) 
                        }}>
                          {bug.severity}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ 
                          ...styles.badge, 
                          backgroundColor: `${getPriorityColor(bug.priority)}20`, 
                          color: getPriorityColor(bug.priority) 
                        }}>
                          {bug.priority}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontSize: '13px' }}>
                          {getUserName(bug.assignedTo)}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleView(bug); }} 
                            title="View"
                          >
                            <Eye size={18} color="#64748b" />
                          </button>
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleEdit(bug); }} 
                            title="Edit"
                          >
                            <Edit2 size={18} color="#3b82f6" />
                          </button>
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleDelete(bug); }} 
                            title="Delete"
                          >
                            <Trash2 size={18} color="#ef4444" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={styles.pagination}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredBugs.length)} of {filteredBugs.length} bugs
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '14px', color: '#64748b' }}>
                  Page {currentPage} of {totalPages}
                </div>
                <button 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={styles.modal} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {showAddModal ? 'Report New Bug' : 'Edit Bug'}
              </h2>
              <button 
                style={{ ...styles.actionButton, padding: '8px' }} 
                onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit}>
              <div style={styles.modalBody}>
                {formErrors.length > 0 && (
                  <div style={styles.errorList}>
                    {formErrors.map((error, index) => (
                      <div key={index} style={styles.errorItem}>
                        <AlertCircle size={14} />
                        {error}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                    <label style={styles.label}>Bug Title *</label>
                    <input 
                      type="text" 
                      name="title" 
                      style={styles.input} 
                      value={formData.title} 
                      onChange={handleInputChange} 
                      required 
                      placeholder="Enter bug title"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Project *</label>
                    <select 
                      name="projectId" 
                      style={styles.input} 
                      value={formData.projectId} 
                      onChange={handleInputChange} 
                      required
                    >
                      <option value="">Select Project</option>
                      {projects.map(project => (
                        <option key={project.id || project._id} value={project.id || project._id}>
                          {project.name || project.projectName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Task (Optional)</label>
                    <select 
                      name="taskId" 
                      style={styles.input} 
                      value={formData.taskId} 
                      onChange={handleInputChange}
                    >
                      <option value="">No Task</option>
                      {tasks.map(task => (
                        <option key={task.id || task._id} value={task.id || task._id}>
                          {task.title || task.taskName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Status</label>
                    <select 
                      name="status" 
                      style={styles.input} 
                      value={formData.status} 
                      onChange={handleInputChange}
                    >
                      <option value="Open">Open</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Resolved">Resolved</option>
                      <option value="Closed">Closed</option>
                      <option value="Reopened">Reopened</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Severity</label>
                    <select 
                      name="severity" 
                      style={styles.input} 
                      value={formData.severity} 
                      onChange={handleInputChange}
                    >
                      <option value="Critical">Critical</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Priority</label>
                    <select 
                      name="priority" 
                      style={styles.input} 
                      value={formData.priority} 
                      onChange={handleInputChange}
                    >
                      <option value="Critical">Critical</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Assign To (Optional)</label>
                    <select 
                      name="assignedTo" 
                      style={styles.input} 
                      value={formData.assignedTo} 
                      onChange={handleInputChange}
                    >
                      <option value="">Unassigned</option>
                      {users.map(user => (
                        <option key={user.id || user._id} value={user.id || user._id}>
                          {user.name || user.fullName} {user.email ? `(${user.email})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Environment</label>
                    <input 
                      type="text" 
                      name="environment" 
                      style={styles.input} 
                      value={formData.environment} 
                      onChange={handleInputChange}
                      placeholder="e.g., Production, Staging, Development"
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Description *</label>
                  <textarea 
                    name="description" 
                    style={styles.textarea} 
                    value={formData.description} 
                    onChange={handleInputChange}
                    placeholder="Describe the bug..."
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Steps to Reproduce</label>
                  <textarea 
                    name="stepsToReproduce" 
                    style={styles.textarea} 
                    value={formData.stepsToReproduce} 
                    onChange={handleInputChange}
                    placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Expected Behavior</label>
                  <textarea 
                    name="expectedBehavior" 
                    style={styles.textarea} 
                    value={formData.expectedBehavior} 
                    onChange={handleInputChange}
                    placeholder="What should happen..."
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Actual Behavior</label>
                  <textarea 
                    name="actualBehavior" 
                    style={styles.textarea} 
                    value={formData.actualBehavior} 
                    onChange={handleInputChange}
                    placeholder="What actually happens..."
                  />
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button 
                  type="button" 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => { setShowAddModal(false); setShowEditModal(false); }} 
                  disabled={formSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.button} disabled={formSubmitting}>
                  {formSubmitting ? 'Saving...' : (showAddModal ? 'Report Bug' : 'Update Bug')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedBug && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Bug Details</h2>
              <button 
                style={{ ...styles.actionButton, padding: '8px' }} 
                onClick={() => setShowViewModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Bug ID</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedBug.bugId}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div>
                  <span style={{ 
                    ...styles.badge, 
                    backgroundColor: `${selectedBug.getStatusColor()}20`, 
                    color: selectedBug.getStatusColor() 
                  }}>
                    {selectedBug.status}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Severity</div>
                  <span style={{ 
                    ...styles.badge, 
                    backgroundColor: `${selectedBug.getSeverityColor()}20`, 
                    color: selectedBug.getSeverityColor() 
                  }}>
                    {selectedBug.severity}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Priority</div>
                  <span style={{ 
                    ...styles.badge, 
                    backgroundColor: `${selectedBug.getPriorityColor()}20`, 
                    color: selectedBug.getPriorityColor() 
                  }}>
                    {selectedBug.priority}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Project</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {getProjectName(selectedBug.projectId)}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Task</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {getTaskName(selectedBug.taskId)}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Assigned To</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {getUserName(selectedBug.assignedTo)}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Environment</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedBug.environment || 'Not specified'}
                  </div>
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Title</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                    {selectedBug.title}
                  </div>
                </div>
              </div>

              {selectedBug.description && (
                <div style={styles.infoSection}>
                  <div style={styles.sectionTitle}>Description</div>
                  <div style={{ fontSize: '14px', color: '#1e293b', whiteSpace: 'pre-wrap' }}>
                    {selectedBug.description}
                  </div>
                </div>
              )}

              {selectedBug.stepsToReproduce && (
                <div style={styles.infoSection}>
                  <div style={styles.sectionTitle}>Steps to Reproduce</div>
                  <div style={{ fontSize: '14px', color: '#1e293b', whiteSpace: 'pre-wrap' }}>
                    {selectedBug.stepsToReproduce}
                  </div>
                </div>
              )}

              {selectedBug.expectedBehavior && (
                <div style={styles.infoSection}>
                  <div style={styles.sectionTitle}>Expected Behavior</div>
                  <div style={{ fontSize: '14px', color: '#1e293b', whiteSpace: 'pre-wrap' }}>
                    {selectedBug.expectedBehavior}
                  </div>
                </div>
              )}

              {selectedBug.actualBehavior && (
                <div style={styles.infoSection}>
                  <div style={styles.sectionTitle}>Actual Behavior</div>
                  <div style={{ fontSize: '14px', color: '#1e293b', whiteSpace: 'pre-wrap' }}>
                    {selectedBug.actualBehavior}
                  </div>
                </div>
              )}

              <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', fontSize: '12px', color: '#64748b' }}>
                <div>Reported: {new Date(selectedBug.createdAt).toLocaleString()}</div>
                {selectedBug.resolvedDate && (
                  <div style={{ marginTop: '4px' }}>
                    Resolved: {new Date(selectedBug.resolvedDate).toLocaleString()}
                  </div>
                )}
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button 
                style={{ ...styles.button, ...styles.secondaryButton }} 
                onClick={() => { setShowViewModal(false); handleEdit(selectedBug); }}
              >
                <Edit2 size={16} /> Edit Bug
              </button>
              <button style={styles.button} onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BugsPage;